import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import PentagonOutlinedIcon from '@mui/icons-material/PentagonOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PhotoOutlinedIcon from '@mui/icons-material/PhotoOutlined';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { colors, typography } from '../../styles/theme';
import RecordCommercialLine from '../../Leads/components/RecordCommercialLine';
import { hasCommercialSignal } from '../../Leads/commercialSummary';
import { buildRecordLocationLabel } from '../utils/recordLocation';
import { formatUserLotCommercial } from '../utils/userLotFormUtils';
import { getUnitListLines } from '../utils/userLotUnits';
import { formatLotContentTooltip } from '../utils/lotContentSummary';

/** Mismos tonos que el chrome de las cards de lote. No son colores nuevos. */
const TEXT_PRIMARY = '#D4D4D4';
const TEXT_SECONDARY = '#ABABAB';

const ellipsisSx = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  minWidth: 0,
};

const metaSx = {
  fontSize: '12px',
  lineHeight: '16px',
  fontWeight: 400,
  color: TEXT_SECONDARY,
};

const titleSx = {
  color: TEXT_PRIMARY,
  fontFamily: typography.fontFamily.heading,
  fontWeight: 600,
  fontSize: '14px',
  lineHeight: '18px',
};

/** Alto de la primera línea. Chevron y ⋮ usan esto para quedar a la altura del título. */
export const RECORD_CARD_TITLE_LINE = 18;

export const recordCardHeaderIconSx = {
  p: 0,
  width: RECORD_CARD_TITLE_LINE,
  height: RECORD_CARD_TITLE_LINE,
  minWidth: RECORD_CARD_TITLE_LINE,
  minHeight: RECORD_CARD_TITLE_LINE,
};

/** Línea de lugar y línea comercial, aunque no haya dato: las cards colapsadas miden igual. */
const RESERVED_LINE = 16;
/** La flecha de unidades vive en esta línea. Un poco más alta que el texto para centrarla. */
const LOCATION_LINE = 24;

/**
 * «Nº catastral 1101…». Vacío si el lote no trae número.
 * @param {object|null|undefined} lot
 * @returns {string}
 */
export function formatCadastralLabel(lot) {
  const name = String(lot?.lot_name || lot?.lot_id || '').trim();
  return name ? `Nº catastral ${name}` : '';
}

/**
 * Identidad de un lote en Registros y en el detalle de lista.
 * El título es siempre «Nº catastral …». La calle va en la línea de lugar,
 * junto al resto de la dirección. Esa línea y la comercial reservan alto
 * aunque no haya dato, para que las cards colapsadas midan igual.
 * El marcador es el puntito de siempre.
 */
/**
 * Ícono de «Contenido de lote» (pentágono teal) con el resumen en el tooltip:
 * «Contenido de lote», título, precio · operación y medios («2 fotos»).
 * Lo usan la card agrupada (junto al número catastral) y la card por registro
 * (junto al título).
 */
export function RecordLotContentIcon({ summary = null, color = colors.primaryLight }) {
  return (
    <Tooltip
      title={<Box component="span" sx={{ whiteSpace: 'pre-line' }}>{formatLotContentTooltip(summary)}</Box>}
      placement="top"
      arrow enterTouchDelay={0} leaveTouchDelay={4000}>
      <Box
        component="span"
        aria-label="Contenido de lote"
        data-testid="record-lot-content-icon"
        onClick={(e) => e.stopPropagation()}
        sx={{ display: 'inline-flex', flexShrink: 0, color, lineHeight: 0 }}
      >
        <PentagonOutlinedIcon sx={{ fontSize: 16 }} />
      </Box>
    </Tooltip>
  );
}

export default function RecordLotIdentity({
  lot,
  unitCount = 0,
  showMarker = false,
  markerColor = colors.primary,
  /** Contenido de lote: ícono junto al título. El texto va en el tooltip. */
  showLotContent = false,
  /** Resumen de `getLotContentSummary(lot)`: solo el tooltip del ícono. La fila va en el collapse. */
  lotContent = null,
  lotContentColor = colors.primaryLight,
  commercialProps = null,
  /** Flecha de unidades, en la línea de lugar. Sin esto el título no reserva columna. */
  locationAdornment = null,
  /** Click en la flecha o el subtítulo: desplegar. No abre el detalle. */
  onLocationClick = null,
  expanded = false,
  /**
   * Línea secundaria en el lugar reservado de la comercial, cuando no hay
   * `commercialProps` (p. ej. hora de la visita en Historial).
   */
  metaLine = null,
  children = null,
}) {
  const cadastralName = String(lot?.lot_name || lot?.lot_id || '').trim();
  const title = formatCadastralLabel(lot) || '—';
  const location = buildRecordLocationLabel(lot);
  const countLabel = unitCount > 0
    ? `${unitCount} ${unitCount === 1 ? 'unidad' : 'unidades'}`
    : '';

  return (
    <Box sx={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0, height: RECORD_CARD_TITLE_LINE }}>
        <Typography
          title={title}
          aria-label={cadastralName ? `Número catastral ${cadastralName}` : undefined}
          sx={{
            ...titleSx,
            ...ellipsisSx,
            flex: '0 1 auto',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {title}
        </Typography>
        {showMarker ? (
          <Box
            component="span"
            aria-label="Marcador"
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              bgcolor: markerColor,
              flexShrink: 0,
            }}
          />
        ) : null}
        {showLotContent ? <RecordLotContentIcon summary={lotContent} color={lotContentColor} /> : null}
        <Box sx={{ flex: '1 1 auto' }} />
        {countLabel ? (
          <Typography component="span" sx={{ ...metaSx, flexShrink: 0, fontWeight: 500 }}>
            {countLabel}
          </Typography>
        ) : null}
      </Box>

      <Box
        onClick={onLocationClick ? (e) => { e.stopPropagation(); onLocationClick(e); } : undefined}
        onKeyDown={onLocationClick ? (e) => {
          if (e.key !== 'Enter' && e.key !== ' ') return;
          e.preventDefault();
          e.stopPropagation();
          onLocationClick(e);
        } : undefined}
        role={onLocationClick ? 'button' : undefined}
        tabIndex={onLocationClick ? 0 : undefined}
        aria-expanded={onLocationClick ? expanded : undefined}
        aria-label={onLocationClick ? (expanded ? 'Ocultar unidades' : 'Ver unidades') : undefined}
        sx={{
          minHeight: LOCATION_LINE,
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          minWidth: 0,
          ...(onLocationClick ? { cursor: 'pointer' } : {}),
        }}
      >
        {locationAdornment}
        {location ? (
          <Typography title={location} sx={{ ...metaSx, ...ellipsisSx, flex: '1 1 auto' }}>
            {location}
          </Typography>
        ) : null}
      </Box>

      <Box sx={{ minHeight: RESERVED_LINE, display: 'flex', alignItems: 'center', minWidth: 0 }}>
        {commercialProps ? (
          <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
            <RecordCommercialLine {...commercialProps} />
          </Box>
        ) : metaLine ? (
          <Typography component="span" sx={{ ...metaSx, ...ellipsisSx, flex: '1 1 auto' }}>
            {metaLine}
          </Typography>
        ) : null}
      </Box>
      {children}
    </Box>
  );
}

const mediaIconSx = { fontSize: 13 };

/** Contadores de fotos / videos / adjuntos. Solo los > 0. */
function LotContentMediaCounts({ summary }) {
  const items = [
    { key: 'images', n: summary.images, Icon: PhotoOutlinedIcon, one: 'foto', many: 'fotos' },
    { key: 'videos', n: summary.videos, Icon: VideocamOutlinedIcon, one: 'video', many: 'videos' },
    { key: 'attachments', n: summary.attachments, Icon: AttachFileIcon, one: 'adjunto', many: 'adjuntos' },
  ].filter((item) => item.n > 0);
  if (!items.length) return null;
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
      {items.map(({ key, n, Icon, one, many }) => (
        <Box
          key={key}
          component="span"
          aria-label={`${n} ${n === 1 ? one : many}`}
          sx={{ ...metaSx, display: 'inline-flex', alignItems: 'center', gap: '2px', fontVariantNumeric: 'tabular-nums' }}
        >
          <Icon aria-hidden sx={mediaIconSx} />
          {n}
        </Box>
      ))}
    </Box>
  );
}

/**
 * Fila de contenido del lote dentro del collapse, con la misma UI que la UF:
 * título arriba; precio · operación y contadores de medios debajo.
 */
export function RecordLotContentIdentity({ summary }) {
  if (!summary) return null;
  const headline = summary.title || 'Contenido del lote';
  const hasMedia = summary.images > 0 || summary.videos > 0 || summary.attachments > 0;
  return (
    <Box data-testid="record-lot-content" sx={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <Typography title={headline} sx={{ ...titleSx, ...ellipsisSx }}>
        {headline}
      </Typography>
      {summary.listing || hasMedia ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
          {summary.listing ? (
            <Typography title={summary.listing} sx={{ ...metaSx, ...ellipsisSx, flex: '0 1 auto' }}>
              {summary.listing}
            </Typography>
          ) : null}
          {hasMedia ? <LotContentMediaCounts summary={summary} /> : null}
        </Box>
      ) : null}
    </Box>
  );
}

/** Flecha de unidades, centrada en el subtítulo. El click lo toma la línea. */
export function RecordExpandButton({ expanded }) {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        borderRadius: '4px',
        color: expanded ? colors.primary : TEXT_SECONDARY,
        transition: 'color 0.15s ease',
        // El glifo no llega al borde del viewBox: sin esto la flecha queda más adentro que el título.
        ml: '-7px',
        '&:hover': {
          color: colors.primary,
          bgcolor: 'rgba(255,255,255,0.08)',
        },
      }}
    >
      <ExpandMoreIcon
        aria-hidden
        sx={{
          fontSize: 22,
          transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
          transition: 'transform 0.15s ease',
        }}
      />
    </Box>
  );
}

/**
 * Fila de UF dentro del lote. Etiqueta y título de publicación no van pegados.
 * Precio / stock / operación en su línea; la etapa comercial en la suya,
 * para que ninguna se coma a la otra.
 */
export function RecordUnitIdentity({ unit, location = '', titleAdornment = null, showZero = false }) {
  const { headline, subtitle } = getUnitListLines(unit);
  const listing = formatUserLotCommercial(unit);
  const summary = unit?.commercial_summary;
  return (
    <Box sx={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, minWidth: 0 }}>
        <Typography sx={titleSx}>
          {headline}
        </Typography>
        {titleAdornment}
      </Box>
      {subtitle ? (
        <Typography sx={{ ...metaSx, ...ellipsisSx }} title={subtitle}>
          {subtitle}
        </Typography>
      ) : null}
      {location ? (
        <Typography title={location} sx={{ ...metaSx, ...ellipsisSx }}>
          {location}
        </Typography>
      ) : null}
      {listing ? (
        <Typography sx={{ ...metaSx, ...ellipsisSx }} title={listing}>
          {listing}
        </Typography>
      ) : null}
      {hasCommercialSignal(summary) || (showZero && summary) ? <RecordCommercialLine summary={summary} showZero={showZero} /> : null}
    </Box>
  );
}
